import React, { useRef, useState } from 'react';
import { adminApi } from '../api';
import { STATUS } from '../../styles/tokens';
import { RunningLoader } from '@/components/RunningLoader';

interface Props {
  prompt: string;
  styleName: string;
  onClose: () => void;
  onSaveAsThumbnail: (url: string) => void;
}

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const TransferStyleTestModal: React.FC<Props> = ({ prompt, styleName, onClose, onSaveAsThumbnail }) => {
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [sourcePreview, setSourcePreview] = useState<string>('');
  const [extraRequirements, setExtraRequirements] = useState('');
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [resultUrl, setResultUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handlePickFile = (file: File | null) => {
    if (!file) return;
    if (!ALLOWED_MIMES.includes(file.type)) {
      setError('僅支援 JPEG / PNG / WebP / GIF');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError('檔案大小不能超過 10 MB');
      return;
    }
    setError('');
    setSourceFile(file);
    setSourcePreview(URL.createObjectURL(file));
    setResultUrl('');
  };

  const handleGenerate = async () => {
    if (!sourceFile) return setError('請先選擇測試圖片');
    if (!prompt.trim()) return setError('Prompt 不能為空');
    setLoading(true);
    setError('');
    setResultUrl('');
    try {
      const { imageUrl } = await adminApi.testTransferStyle({
        image: sourceFile,
        prompt,
        name: styleName || '測試',
        extraRequirements: extraRequirements.trim() || undefined,
      });
      setResultUrl(imageUrl);
    } catch (e: any) {
      setError(e.message || '生成失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">測試 Prompt — {styleName || '未命名風格'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex">
          {/* 左欄：輸入 */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 border-r dark:border-gray-700">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5">測試圖片</label>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={e => { handlePickFile(e.target.files?.[0] ?? null); e.target.value = ''; }}
                className="hidden"
              />
              {sourcePreview ? (
                <div className="space-y-2">
                  <img src={sourcePreview} alt="測試圖" className="w-full max-h-64 object-contain rounded border dark:border-gray-600 bg-gray-50 dark:bg-gray-900" />
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="text-xs text-blue-500 hover:text-blue-700"
                  >更換圖片</button>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="w-full border-2 border-dashed dark:border-gray-600 rounded-lg py-10 text-sm text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  點此選擇測試圖片
                </button>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-500 dark:text-gray-400">當前 Prompt（唯讀）</label>
                <button onClick={() => setShowFullPrompt(s => !s)} className="text-xs text-blue-500 hover:text-blue-700">
                  {showFullPrompt ? '收起' : '展開'}
                </button>
              </div>
              <pre className={`text-[11px] font-mono whitespace-pre-wrap break-words bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300 border dark:border-gray-700 rounded p-2 ${showFullPrompt ? '' : 'max-h-24 overflow-hidden'}`}>
                {prompt || <span className="italic text-gray-400">（尚未輸入 prompt）</span>}
              </pre>
            </div>

            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5">額外要求（選填）</label>
              <textarea
                rows={3}
                value={extraRequirements}
                onChange={e => setExtraRequirements(e.target.value)}
                placeholder="例如：保持人臉清晰、背景改為夜景..."
                className="w-full text-sm border dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
              />
            </div>

            {error && (
              <p className={`text-sm ${STATUS.error} px-3 py-2 rounded`}>{error}</p>
            )}

            <button
              onClick={handleGenerate}
              disabled={loading || !sourceFile || !prompt.trim()}
              className="w-full text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? '生成中... 約需 20–40 秒' : '開始測試生成'}
            </button>
          </div>

          {/* 右欄：結果 */}
          <div className="w-[28rem] shrink-0 flex flex-col px-5 py-4 space-y-3 overflow-y-auto bg-gray-50 dark:bg-gray-900/40">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">生成結果</p>
            {loading && (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                <RunningLoader label="Gemini 思考中" />
              </div>
            )}
            {!loading && !resultUrl && (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-400 italic">
                （尚未測試）
              </div>
            )}
            {!loading && resultUrl && (
              <>
                <div className="grid grid-cols-2 gap-2 text-[10px] text-gray-400">
                  <span className="text-center">原圖</span>
                  <span className="text-center">結果</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <img src={sourcePreview} alt="原圖" className="w-full aspect-square object-cover rounded border dark:border-gray-700 bg-white dark:bg-gray-900" />
                  <img src={resultUrl} alt="結果" className="w-full aspect-square object-cover rounded border dark:border-gray-700 bg-white dark:bg-gray-900" />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <a
                    href={resultUrl}
                    download={`style-test-${Date.now()}.png`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs border dark:border-gray-600 px-3 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300"
                  >下載</a>
                  <button
                    onClick={() => { onSaveAsThumbnail(resultUrl); }}
                    className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded hover:bg-emerald-700 transition-colors"
                  >另存為這個風格的縮圖</button>
                  <button
                    onClick={handleGenerate}
                    className="text-xs text-blue-500 hover:text-blue-700 ml-auto"
                  >重新生成</button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-5 py-3 border-t dark:border-gray-700">
          <button onClick={onClose} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">關閉</button>
        </div>
      </div>
    </div>
  );
};

export default TransferStyleTestModal;
